'use strict';

import $ from 'jquery';
import debounce from 'lodash/debounce';

/**
 * Recommends a ski length based on the skier weight the customer enters.
 *
 * INTERCONT-7216 point 1. Nothing here is new data: the skier weight input is
 * one of the platform "Lisäkysymykset" number questions already rendered inside
 * `.product-questions`, and the weight range of every length already ships on
 * the size radios as `data-attributes` (see package-builder.js, which filters
 * package ski options off the same data). This module only compares the two and
 * writes a sentence into the container rendered by product-color-level.js.
 *
 * @param {jQuery} $product product card(s) to bind to
 */
export default function ($product) {
	// NOTE: `data-unit` is CMS-authored (Site.getQuestions), so it is this
	// feature's single point of failure. `data-type` is load-bearing too:
	// click-and-collect rebuilds copies of these questions in its reservation
	// form carrying data-unit/data-label but no data-type.
	const WEIGHT_INPUT = '.product-questions input[data-type="number"][data-unit="kg"]';
	const COLOR_INPUT = '[name*="color"]:checked, [type="hidden"][name*="color"][data-page-variant="true"]';

	$product.find('.product-form').each(function () {
		const $form = $(this);
		const $container = $form.find('.ski-length-recommendation');
		const $weight = $form.find(WEIGHT_INPUT).first();

		if (!$container.length || !$weight.length) {
			return;
		}

		// Whether the value the field currently holds has been committed (left
		// or changed) rather than typed, so re-renders triggered by something
		// other than the customer typing can keep showing what they were shown.
		let lastShowAll = false;

		// While typing, only complain about values that cannot become valid by
		// typing more (201 kg, 65.5 kg). A too-low value is usually a prefix -
		// "6" on the way to "60" - so that one waits until the field is left.
		const renderWhileTyping = debounce(function () {
			render(false);
		}, 400);

		$form.on('input', WEIGHT_INPUT, renderWhileTyping);
		$form.on('change focusout', WEIGHT_INPUT, function () {
			// Drop any pending typing render, which would otherwise land after
			// this one and wipe the hint it is about to show.
			renderWhileTyping.cancel();
			render(true);
		});
		$form.on('change productColorChange', '[name*="color"]', function () {
			// A colour change is not new typing, so it must not downgrade a hint
			// the customer has already been shown: mousedown on a swatch blurs
			// the weight input first, and passing `false` here would clear the
			// hint that focusout had just rendered.
			render(lastShowAll);
		});

		// Value can already be present on load (bfcache, autofill, back button)
		render(false);

		/**
		 * Size radios of the currently selected colour.
		 * Selecting by colour rather than by `:not(.hidden)` keeps this
		 * independent of whether product-page.js has run its colour handler yet.
		 * @return {jQuery}
		 */
		function sizeInputs() {
			const color = $form.find(COLOR_INPUT).val();
			const $list = color
				? $form.find('.product-sizes-list[data-color="' + color + '"]')
				: $();

			// Extra sale products use a <select name="ean">, which carries none
			// of the data we need - restricting to inputs keeps them out.
			return ($list.length ? $list : $form.find('.product-sizes-list'))
				.find('input[name="ean"]');
		}

		/**
		 * @param {String} text
		 * @param {Boolean} [isError] render as a validation hint, not a result
		 */
		function setMessage(text, isError) {
			$container.toggleClass('ski-length-recommendation--error', !!text && !!isError);
			$weight.toggleClass('invalid', !!text && !!isError);

			// Skip no-op writes so the aria-live region is not re-announced on
			// every keystroke.
			if ($container.text() !== text) {
				$container.text(text);
			}
		}

		/**
		 * Validation hint for the weight the customer has entered, or '' when
		 * there is nothing to say. Limits come from the input's own min/max so
		 * the copy stays correct if they are changed in Stage.
		 * @param {Boolean} showAll include hints that a further keystroke could fix
		 * @return {String}
		 */
		function validationHint(showAll) {
			const validity = $weight[0].validity;

			if (validity.rangeOverflow) {
				return ($container.data('maxText') || '').replace('%s', $weight.attr('max'));
			}
			if (validity.stepMismatch || validity.badInput) {
				return $container.data('stepText') || '';
			}
			// Reachable by typing another digit, so only shown once the customer
			// has committed the value.
			if (showAll && validity.rangeUnderflow) {
				return ($container.data('minText') || '').replace('%s', $weight.attr('min'));
			}
			return '';
		}

		/**
		 * Emptiness has to be checked before Number(), because Number('') is 0
		 * rather than NaN - a bound treated as 0 would silently never match.
		 * @param {String} value
		 * @return {Boolean}
		 */
		function hasBound(value) {
			return value !== '' && value !== null && value !== undefined;
		}

		/**
		 * A missing bound on one side means open-ended.
		 * @param {String} value
		 * @param {Number} fallback
		 * @return {Number}
		 */
		function bound(value, fallback) {
			return hasBound(value) ? Number(value) : fallback;
		}

		/**
		 * @param {Number[]} lengths
		 * @return {String} e.g. "187, 192 ja 197"
		 */
		function formatLengths(lengths) {
			if (lengths.length < 2) {
				return String(lengths[0]);
			}
			return lengths.slice(0, -1).join(', ') + ' ja ' + lengths[lengths.length - 1];
		}

		/**
		 * @param {Boolean} showAll show validation hints the customer could still
		 *   type their way out of (i.e. the field was committed or left)
		 */
		function render(showAll) {
			lastShowAll = showAll;

			const $inputs = sizeInputs();

			// No size radios at all: the feature does not apply to this product,
			// which is not the same as "no length fits you".
			if (!$inputs.length) {
				return setMessage('');
			}

			// An untouched required field is invalid, but saying so unprompted
			// would scold the customer before they have done anything. Input
			// the browser cannot parse at all ("1e", ".") also reports an empty
			// value, so it has to be let through here - `value === ''` alone
			// cannot tell an untouched field from an unparseable one.
			if ($weight.val() === '' && !$weight[0].validity.badInput) {
				return setMessage('');
			}

			if (!$weight[0].checkValidity()) {
				return setMessage(validationHint(showAll), true);
			}

			const weight = Number($weight.val());
			const matches = [];
			let usable = 0;

			$inputs.each(function () {
				const $input = $(this);
				const data = $input.data();
				const range = (data.attributes || {}).user_weight;
				const length = Number(data.numeric);

				if (!range || !isFinite(length)) {
					return;
				}

				// PIM emits `{"user_weight":{}}` for a variation with no data.
				// Neither bound means missing data, not "fits everyone" - the
				// same rule getAttributeTable() applies to the table server-side.
				if (!hasBound(range.min) && !hasBound(range.max)) {
					return;
				}

				usable++;
				if (weight < bound(range.min, -Infinity) || weight > bound(range.max, Infinity)) {
					return;
				}

				matches.push({
					length,
					// Same definition of unavailable the page itself uses
					buyable: !!(data.availableInWeb || data.availableInStores || data.availableForPreorder)
				});
			});

			// product.variationAttributes is built across every colour, so the
			// container can render for a colour whose own sizes carry no weight
			// data. That is "not applicable", not "nothing fits you".
			if (!usable) {
				return setMessage('');
			}

			if (!matches.length) {
				return setMessage($container.data('noMatchText') || '');
			}

			const buyable = matches.filter(match => match.buyable);
			const lengths = (buyable.length ? buyable : matches)
				.map(match => match.length)
				.filter((length, i, all) => all.indexOf(length) === i)
				.sort((a, b) => a - b);

			setMessage(
				($container.data(lengths.length > 1 ? 'multipleText' : 'singleText') || '')
					.replace('%s', formatLengths(lengths))
			);
		}
	});
}
